import { useParams } from 'react-router-dom';

import { Icon, type IconName, Stack, Text } from '@eluve/components';
import { type MessageKey, useTranslation } from '@eluve/i18n';

export type ToolInvocation = {
  toolCallId: string;
  toolName: string;
  state: 'input-streaming' | 'input-available' | 'output-available';
  input?: unknown;
  output?: unknown;
};

interface NoteEntry {
  sourceTimestamp?: string;
  appointmentId?: string;
  appointmentName?: string;
}

interface ToolDisplayConfig {
  activeLabel: MessageKey;
  completedLabel: MessageKey;
  icon: IconName;
}

const toolConfig: Record<string, ToolDisplayConfig> = {
  getPatientNotes: {
    activeLabel: 'chat.tools.getPatientNotesActive',
    completedLabel: 'chat.tools.getPatientNotesCompleted',
    icon: 'Search',
  },
  createSpecTemplate: {
    activeLabel: 'chat.tools.createSpecTemplateActive',
    completedLabel: 'chat.tools.createSpecTemplateCompleted',
    icon: 'WandSparkles',
  },
  saveSpecTemplate: {
    activeLabel: 'chat.tools.saveSpecTemplateActive',
    completedLabel: 'chat.tools.saveSpecTemplateCompleted',
    icon: 'Save',
  },
  draftDocument: {
    activeLabel: 'chat.tools.draftDocumentActive',
    completedLabel: 'chat.tools.draftDocumentCompleted',
    icon: 'FileText',
  },
  generateDocument: {
    activeLabel: 'chat.tools.generateDocumentActive',
    completedLabel: 'chat.tools.generateDocumentCompleted',
    icon: 'FileText',
  },
  getAppointmentNote: {
    activeLabel: 'chat.tools.getAppointmentNoteActive',
    completedLabel: 'chat.tools.getAppointmentNoteCompleted',
    icon: 'FileText',
  },
  updateAppointmentNote: {
    activeLabel: 'chat.tools.updateAppointmentNoteActive',
    completedLabel: 'chat.tools.updateAppointmentNoteCompleted',
    icon: 'FileEdit',
  },
  getCustomInstructions: {
    activeLabel: 'chat.tools.getCustomInstructionsActive',
    completedLabel: 'chat.tools.getCustomInstructionsCompleted',
    icon: 'Settings',
  },
  suggestCustomInstruction: {
    activeLabel: 'chat.tools.suggestCustomInstructionActive',
    completedLabel: 'chat.tools.suggestCustomInstructionCompleted',
    icon: 'Settings',
  },
  saveCustomInstruction: {
    activeLabel: 'chat.tools.saveCustomInstructionActive',
    completedLabel: 'chat.tools.saveCustomInstructionCompleted',
    icon: 'Save',
  },
  queryFhirResources: {
    activeLabel: 'chat.tools.queryFhirResourcesActive',
    completedLabel: 'chat.tools.queryFhirResourcesCompleted',
    icon: 'Stethoscope',
  },
  // Supervisor delegation tool calls (agent-{key} from agents map)
  'agent-patientNotesAgent': {
    activeLabel: 'chat.tools.patientNotesAgentActive',
    completedLabel: 'chat.tools.patientNotesAgentCompleted',
    icon: 'Search',
  },
  'agent-conversationStarterAgent': {
    activeLabel: 'chat.tools.conversationStarterAgentActive',
    completedLabel: 'chat.tools.conversationStarterAgentCompleted',
    icon: 'MessageSquarePlus',
  },
};

const defaultToolConfig: ToolDisplayConfig = {
  activeLabel: 'chat.tools.defaultActive',
  completedLabel: 'chat.tools.defaultCompleted',
  icon: 'WandSparkles',
};

function getToolConfig(toolName: string): ToolDisplayConfig {
  return toolConfig[toolName] ?? defaultToolConfig;
}

function getToolLabelKey(toolInvocation: ToolInvocation): MessageKey {
  const config = getToolConfig(toolInvocation.toolName);
  return toolInvocation.state === 'output-available'
    ? config.completedLabel
    : config.activeLabel;
}

function getRetrievedNotes(toolInvocation: ToolInvocation): NoteEntry[] | null {
  if (
    toolInvocation.toolName !== 'getPatientNotes' ||
    toolInvocation.state !== 'output-available'
  ) {
    return null;
  }
  try {
    const output = toolInvocation.output;
    if (Array.isArray(output)) {
      return output.filter(
        (n: NoteEntry) => n.sourceTimestamp ?? n.appointmentId,
      );
    }
  } catch {
    // ignore parse errors
  }
  return null;
}

export const ToolCallStatus = ({
  toolInvocation,
}: {
  toolInvocation: ToolInvocation;
}) => {
  const { t } = useTranslation();
  const { tenantId } = useParams<{ tenantId: string }>();
  const isComplete = toolInvocation.state === 'output-available';
  const label = t(getToolLabelKey(toolInvocation));
  const config = getToolConfig(toolInvocation.toolName);
  const retrievedNotes = getRetrievedNotes(toolInvocation);

  return (
    <Stack gap={1}>
      <div
        className={`inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1.5 transition-all duration-300 ${
          isComplete
            ? 'border-border-positive-subtle bg-bg-positive-subtle'
            : 'border-border-insights-subtle bg-bg-insights-subtle'
        }`}
      >
        {isComplete ? (
          <Icon name="CheckCircle2" className="text-text-positive" />
        ) : (
          <Icon
            name={config.icon}
            className="animate-pulse text-text-insights"
          />
        )}
        <Text size="xs" color={isComplete ? 'secondary' : 'primary'}>
          {label}
          {!isComplete && '…'}
        </Text>
      </div>
      {retrievedNotes && retrievedNotes.length > 0 && (
        <Stack className="mt-0.5 ml-4">
          {retrievedNotes.map((note, i) => {
            const date = note.sourceTimestamp
              ? new Date(note.sourceTimestamp).toLocaleDateString()
              : null;
            const name = note.appointmentName ?? null;
            const noteLabel =
              name && date
                ? `${name} - ${date}`
                : (name ?? date ?? t('chat.tools.appointmentNote'));
            const href =
              tenantId && note.appointmentId
                ? `/organizations/${tenantId}/appointments/${note.appointmentId}`
                : undefined;

            return href ? (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-text-tertiary underline hover:text-text-primary"
              >
                {noteLabel}
              </a>
            ) : (
              <Text inline key={i} size="xs" color="tertiary">
                {noteLabel}
              </Text>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};
